import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  TextField,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { TagMappingDto, saveOrEditTagMapping } from '../../Service/AxiosService';

const DROPDOWN_WIDTH = '100%';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AddTagMapping = (props: {
  open: boolean,
  onClose: () => void,
  crmTags: { tagId: string, tagName: string }[],
  cerboTags: any[],
  customFields: { fieldId: string, fieldName: string }[],
  initialValues?: any
}) => {

  // Normalize incoming props to arrays to avoid runtime errors when API returns unexpected shapes
  const crmTagsArr = Array.isArray(props.crmTags) ? props.crmTags : [];
  const cerboTagsArr = Array.isArray(props.cerboTags) ? props.cerboTags : [];
  const customFieldsArr = Array.isArray(props.customFields) ? props.customFields : [];


  const [appSelection, setAppSelection] = useState<boolean>(false);
  const [addOrRemove, setAddOrRemove] = useState<boolean>(true);
  const [tagOption, setTagOption] = useState<boolean>(false);
  const [customField, setCustomField] = useState<string>('');
  const [customFieldValue, setCustomFieldValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addCrmTags, setAddCrmTags] = useState<string[]>([]);
  const [addCerboTags, setAddCerboTags] = useState<string[]>([]);
  const [removeCrmTags, setRemoveCrmTags] = useState<string[]>([]);
  const [removeCerboTags, setRemoveCerboTags] = useState<string[]>([]);

  // Initialise the form only ONCE per open. Without this guard the effect re-ran whenever the tag/
  // custom-field lists finished loading (they're async props), resetting the form and WIPING a
  // selection the user had already made — so the HL tag saved as NULL.
  const initedRef = useRef(false);

  useEffect(() => {
    if (!props.open) { initedRef.current = false; return; }
    if (initedRef.current) return;
    initedRef.current = true;
    {
      if (props.initialValues) {
        setAppSelection(props.initialValues.isFromCerbo === 'Cerbo Tag');
        setAddOrRemove(props.initialValues.isAdd === 'Add');
        // Initialise from the raw tag ids (not the display name) so an existing selection is preserved
        // reliably and a stray re-render can't wipe it. Falls back to name-matching only if ids are absent.
        setAddCrmTags(
          props.initialValues.addCrmTagId
            ? String(props.initialValues.addCrmTagId).split(',').filter(Boolean)
            : crmTagsArr?.filter(t => props.initialValues.addCrmTag?.includes(t.tagName))?.map(t => t.tagId) || []
        );
        setRemoveCrmTags(
          props.initialValues.removeCrmTagId
            ? String(props.initialValues.removeCrmTagId).split(',').filter(Boolean)
            : crmTagsArr?.filter(t => props.initialValues.removeCrmTag?.includes(t.tagName))?.map(t => t.tagId) || []
        );
      setAddCerboTags(cerboTagsArr?.filter(t => props.initialValues.CerboAddTags?.includes(`${t.tagCategory} - ${t.name}`))?.map(t => t.id) || []);
        setRemoveCerboTags(cerboTagsArr?.filter(t => props.initialValues.CerboRemoveTags?.includes(`${t.tagCategory} - ${t.name}`))?.map(t => t.id) || []);   
        setTagOption(props.initialValues.hasValue === 'Yes');
        setCustomField(
          customFieldsArr.find(f => f.fieldName === props.initialValues.customFieldId)?.fieldId || ''
        );
        setCustomFieldValue(props.initialValues.customFieldValue || '');
      }
      else {
        setAppSelection(false);
        setAddOrRemove(true);
        setAddCrmTags([]);
        setRemoveCrmTags([]);
        setAddCerboTags([]);
        setRemoveCerboTags([]);
        setTagOption(false);
        setCustomField('');
        setCustomFieldValue('');
        setError(null);
      }
    }
  }, [props.open, props.initialValues, props.cerboTags, props.customFields]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    // Only persist the tag fields whose dropdown is actually shown for the current Source (HL/Cerbo)
    // + Add/Remove selection. Clearing the hidden side prevents a stale mapping from lingering when an
    // existing rule is switched between Add and Remove (which otherwise left a row showing BOTH).
    const isAdd = addOrRemove === true;
    const isCerboSource = appSelection === true;
    let addCrm = addCrmTags, removeCrm = removeCrmTags;
    let addCerbo = addCerboTags, removeCerbo = removeCerboTags;
    // The Add/Remove toggle is the TRIGGER condition (tag added vs removed), not the action. A rule can
    // carry BOTH an add-tag and a remove-tag action, applied together when the trigger fires. We only clear
    // the field that belongs to the other Source (the trigger side has no second action field of its own).
    if (!isCerboSource) {
      // HL-source: the HL tag (addCrm) is the trigger; its actions are on the Cerbo side (add + remove).
      removeCrm = [];
    } else {
      // Cerbo-source: the Cerbo tag (addCerbo) is the trigger; its actions are on the HL side (add + remove).
      removeCerbo = [];
    }
    const dto: TagMappingDto = {
      AddCrmTagId: addCrm.length > 0 ? addCrm.join(',') : null,
      AddCerboTagId: addCerbo.length > 0 ? addCerbo.join(',') : null,
      RemoveCrmTagId: removeCrm.length > 0 ? removeCrm.join(',') : null,
      RemoveCerboTagId: removeCerbo.length > 0 ? removeCerbo.join(',') : null,
      IsAdd: isAdd,
      IsCerbo: appSelection === true,
      HasValue: tagOption === true,
      CustomFieldId: tagOption === true ? (customField || null) : null,
      CustomFieldValue: tagOption === true && customFieldValue ? customFieldValue : null,
      id: props.initialValues?.id
    };
    const result = await saveOrEditTagMapping(dto);
    setSaving(false);
    if (result.success) {
      props.onClose();
    } else {
      setError(result.error || 'Failed to save');
    }
  };

  // Common styles matching AddTaskList.tsx
  const autocompleteInputSx = {
    bgcolor: '#fff',
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(24,26,42,0.08)',
    '& .MuiInputBase-root': {
      fontSize: 14,
      color: '#23244A',
      borderBottom: '1.5px solid #bdbdbd',
    },
    '& .MuiInputLabel-root': {
      fontSize: 13,
      color: '#23244A',
    },
  };

  const autocompleteSx = {
    width: DROPDOWN_WIDTH,
    bgcolor: '#fff',
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(24,26,42,0.08)',
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: '#FAFAFC',
          boxShadow: '0 2px 16px rgba(24,26,42,0.10)',
          minWidth: 320,
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        Configure Tag Mapping Rule
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2, sm: 3 },
          maxHeight: '80vh',
          overflowY: 'auto',
          minHeight: 0,
          background: '#FAFAFC',
        }}
      >
        <Box sx={{ width: '100%' }}>
            <Box sx={{display: 'flex', gap: 1, position: 'relative'}}>
              <Box className="step-number">1</Box>
              <Box
                sx={{
                  position: 'absolute',
                  left: 19,
                  top: 38,
                  bottom: 0,
                  width: 2,
                  bgcolor: '#e2e8f0',
                }}
              />
                  <Box sx={{width: '100%', mb: 4}}>
                    <Box sx={{
                      bgcolor: '#f3f9ff',
                      px: 2,
                      py: 1,
                      borderRadius: 4,
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>
                        Tag Source Selection
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#555' }}>
                        Choose whether the tag should be added or removed in HL or Cerbo
                      </Typography>
                    </Box>
                    <RadioGroup
                        row
                        value={appSelection}
                        onChange={e => setAppSelection(e.target.value === 'true')}
                        sx={{
                          borderRadius: 2,
                          p: 1,
                          background: '#fff',
                          justifyContent: 'left',
                          gap: 3,
                          ml: 3,
                        }}
                      >
                        <FormControlLabel
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: 13,
                              color: '#475569',
                            },
                          }}
                          value={false}
                          control={
                          <Radio 
                            size="small"
                            sx={{
                              '& .MuiSvgIcon-root': {
                                fontSize: 18,
                              },
                              color: '#94a3b8',
                            }}
                          />
                        }
                          label="HL Tag"
                          componentsProps={{ typography: { variant: 'body2' } }}
                        />
                        <FormControlLabel
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: 13,
                              color: '#475569',
                            },
                          }}
                          value={true}
                          control={
                          <Radio 
                            size="small"
                            sx={{
                              '& .MuiSvgIcon-root': {
                                fontSize: 18,
                              },
                              color: '#94a3b8',
                            }}
                          />
                        }
                          label="Cerbo Tag"
                          componentsProps={{ typography: { variant: 'body2' } }}
                        />
                      </RadioGroup>
                  </Box>
            </Box>
          
          <Box sx={{display: 'flex', gap: 1, position: 'relative'}}>
            <Box className="step-number">2</Box>
            <Box
                sx={{
                  position: 'absolute',
                  left: 19,
                  top: 38,
                  bottom: 0,
                  width: 2,
                  bgcolor: '#e2e8f0',
                }}
              />
            <Box sx={{width: '100%', mb: 4}}>
              <Box sx={{
                bgcolor: '#f3f9ff',
                px: 2,
                py: 1,
                borderRadius: 4,
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>
                  Add or Remove Tag
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#555' }}>
                  Choose whether the tag will be added or removed in the system above selected.
                </Typography>
              </Box>
              <RadioGroup
                row
                value={addOrRemove}
                onChange={e => setAddOrRemove(e.target.value === 'true')}
                sx={{
                  borderRadius: 2,
                  p: 1,
                  background: '#fff',
                  justifyContent: 'left',
                  gap: 3,
                  ml: 3,
                }}
              >
                <FormControlLabel 
                  sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: 13,
                        color: '#475569',
                      },
                    }}
                  value={true} control={
                          <Radio 
                            size="small"
                            sx={{
                              '& .MuiSvgIcon-root': {
                                fontSize: 18,
                              },
                              color: '#94a3b8',
                            }}
                          />
                        } label="Add Tag" 
                  componentsProps={{ typography: { variant: 'body2' } }}/>
                <FormControlLabel 
                  sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: 13,
                        color: '#475569',
                      },
                    }}
                    value={false} control={
                          <Radio 
                            size="small"
                            sx={{
                              '& .MuiSvgIcon-root': {
                                fontSize: 18,
                              },
                              color: '#94a3b8',
                            }}
                          />
                        } label="Remove Tag" 
                  componentsProps={{ typography: { variant: 'body2' } }}/>
              </RadioGroup>
            </Box>
          </Box>
          
          {appSelection === false && (
            <>
              <Box sx={{display: 'flex', gap: 1, position: 'relative'}}>
                <Box className="step-number">3</Box>
                <Box
                    sx={{
                      position: 'absolute',
                      left: 19,
                      top: 38,
                      bottom: 0,
                      width: 2,
                      bgcolor: '#e2e8f0',
                    }}
                  />
                <Box sx={{width: '100%'}}>
                  <Box sx={{
                    bgcolor: '#f3f9ff',
                    px: 2,
                    py: 1,
                    borderRadius: 4,
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>
                      Select HL Tag
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      Choose the HL tag to map with Cerbo tags.
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2} direction="column" sx={{ mt: 3, mb: 4, ml: 3 }}>
                  <Grid>
                      <Autocomplete
                        options={crmTagsArr}
                        getOptionLabel={option => option.tagName}
                        slotProps={{
                          paper: {
                            sx: {
                              '& .MuiAutocomplete-option': {
                                fontSize: 13,
                                minHeight: 32,
                              },
                            },
                          },
                        }}
                        value={crmTagsArr?.find(tag => tag.tagId === addCrmTags[0]) || undefined}
                        onChange={(_, newValue) => setAddCrmTags(newValue ? [newValue.tagId] : [])}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="HL Tag"
                            size="small"
                            fullWidth
                            variant="outlined"
                            sx={{
                              bgcolor: '#fff',
                              borderRadius: 2,
                              '& .MuiOutlinedInput-root': {
                                fontSize: 14,
                                color: '#23244A',
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: '#dedede',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#3D52A0',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#3D52A0',
                                },
                              },

                              '& .MuiInputLabel-root': {
                                fontSize: 13,
                                color: '#23244A',
                              },
                            }}
                          />
                        )}
                        sx={{ width: '50%', bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(24,26,42,0.08)' }}
                        disableClearable
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              <Box sx={{display: 'flex', gap: 1, position: 'relative'}}>
                <Box className="step-number">4</Box>
                <Box
                    sx={{
                      position: 'absolute',
                      left: 19,
                      top: 38,
                      bottom: 0,
                      width: 2,
                      bgcolor: '#e2e8f0',
                    }}
                  />
                <Box sx={{width: '100%'}}>
                  <Box sx={{
                    bgcolor: '#f3f9ff',
                    px: 2,
                    py: 1,
                    borderRadius: 4,
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>
                      Select Cerbo Tag
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      Choose the Cerbo tag to map with HL tags.
                    </Typography>
                  </Box>
                  <Grid container spacing={2} direction="column" sx={{ mt: 3, mb: 4, ml: 3 }}>
                    {addOrRemove && (
                      <Grid>
                        <Autocomplete
                          multiple
                          limitTags={2}
                          disableCloseOnSelect
                          options={cerboTagsArr}
                          getOptionLabel={option => `${option.tagCategory} - ${option.name}`}
                          slotProps={{
                            paper: {
                              sx: {
                                '& .MuiAutocomplete-option': {
                                  fontSize: 13,
                                  minHeight: 32,
                                },
                              },
                            },
                          }}
                          value={cerboTagsArr.filter(tag => addCerboTags.includes(tag.id))}
                          onChange={(_, newValue) => setAddCerboTags(newValue?.map(tag => tag.id) || [])}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.id} style={{height: '34px'}}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 2 }}
                                checked={selected}
                                sx={{
                                '& .MuiSvgIcon-root': {
                                  fontSize: 17,
                                },
                                  color: '#94a3b8',
                              }}
                              />
                              {`${option.tagCategory} - ${option.name}`}
                            </li>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label="Cerbo Add Tag"
                              size="small"
                              fullWidth
                              variant="outlined"
                              sx={{
                              bgcolor: '#fff',
                              borderRadius: 2,
                              '& .MuiOutlinedInput-root': {
                                fontSize: 14,
                                color: '#23244A',
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: '#dedede',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#3D52A0',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#3D52A0',
                                },
                              },

                              '& .MuiInputLabel-root': {
                                fontSize: 13,
                                color: '#23244A',
                              },
                            }}
                            />
                          )}
                          sx={{ width: '50%', bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(24,26,42,0.08)' }}
                        />
                      </Grid>
                    )}
                    <Grid>
                      <Autocomplete
                        multiple
                        limitTags={2}
                        disableCloseOnSelect
                        options={cerboTagsArr}
                        getOptionLabel={option => `${option.tagCategory} - ${option.name}`}
                        slotProps={{
                            paper: {
                              sx: {
                                '& .MuiAutocomplete-option': {
                                  fontSize: 13,
                                  minHeight: 32,
                                },
                              },
                            },
                          }}
                        value={cerboTagsArr.filter(tag => removeCerboTags.includes(tag.id))}
                        onChange={(_, newValue) => setRemoveCerboTags(newValue?.map(tag => tag.id) || [])}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.id} style={{height: '34px'}}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 2 }}
                              checked={selected}
                              sx={{
                                '& .MuiSvgIcon-root': {
                                  fontSize: 17,
                                },
                                  color: '#94a3b8',
                              }}
                            />
                            {`${option.tagCategory} - ${option.name}`}
                          </li>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Cerbo Remove Tag"
                            size="small"
                            fullWidth
                            variant="outlined"
                            sx={{
                              bgcolor: '#fff',
                              borderRadius: 2,
                              '& .MuiOutlinedInput-root': {
                                fontSize: 14,
                                color: '#23244A',
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: '#dedede',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#3D52A0',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#3D52A0',
                                },
                              },

                              '& .MuiInputLabel-root': {
                                fontSize: 13,
                                color: '#23244A',
                              },
                            }}
                          />
                        )}
                        sx={{ width: '50%', bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(24,26,42,0.08)' }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              </>
            )}

          {appSelection === true && (
            <>
            <Box sx={{display: 'flex', gap: 1, position: 'relative'}}>
                <Box className="step-number">3</Box>
                <Box
                    sx={{
                      position: 'absolute',
                      left: 19,
                      top: 38,
                      bottom: 0,
                      width: 2,
                      bgcolor: '#e2e8f0',
                    }}
                  />
                <Box sx={{width: '100%'}}>
                  <Box sx={{
                    bgcolor: '#f3f9ff',
                    px: 2,
                    py: 1,
                    borderRadius: 4,
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>
                      Cerbo Tag
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      Select Cerbo tags to add or remove for this mapping.
                    </Typography>
                  </Box>

                  <Grid container spacing={2} direction="column" sx={{ mt: 3, mb: 4, ml: 3 }}>
                    <Grid>
                      <Autocomplete
                        multiple
                        limitTags={2}
                        disableCloseOnSelect
                        options={cerboTagsArr}
                        getOptionLabel={option => `${option.tagCategory} - ${option.name}`}
                         slotProps={{
                            paper: {
                              sx: {
                                '& .MuiAutocomplete-option': {
                                  fontSize: 13,
                                  minHeight: 32,
                                },
                              },
                            },
                          }}
                          value={cerboTagsArr.filter(tag => addCerboTags.includes(tag.id))}
                        onChange={(_, newValue) => {
                          setAddCerboTags(newValue?.map(tag => tag.id) || []);
                        }}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.id} style={{height: '34px'}}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 2 }}
                              checked={selected}
                              sx={{
                                '& .MuiSvgIcon-root': {
                                  fontSize: 17,
                                },
                                  color: '#94a3b8',
                              }}
                            />
                            {`${option.tagCategory} - ${option.name}`}
                          </li>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Cerbo Tags"
                            size="small"
                            fullWidth
                            placeholder="Search and select tags"
                            variant="outlined"
                            sx={{
                              bgcolor: '#fff',
                              borderRadius: 2,
                              '& .MuiOutlinedInput-root': {
                                fontSize: 14,
                                color: '#23244A',
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: '#dedede',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#3D52A0',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#3D52A0',
                                },
                              },

                              '& .MuiInputLabel-root': {
                                fontSize: 13,
                                color: '#23244A',
                              },
                            }}
                          />
                        )}
                        sx={{ width: '50%', bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(24,26,42,0.08)' }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              <Box sx={{display: 'flex', gap: 1, position: 'relative'}}>
                <Box className="step-number">4</Box>
                <Box
                    sx={{
                      position: 'absolute',
                      left: 19,
                      top: 38,
                      bottom: 0,
                      width: 2,
                      bgcolor: '#e2e8f0',
                    }}
                  />
                <Box sx={{width: '100%'}}>
                  <Box sx={{
                    bgcolor: '#f3f9ff',
                    px: 2,
                    py: 1,
                    borderRadius: 4,
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>
                      Select HL Tags
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      Choose the HL tags to map with Cerbo tags.
                    </Typography>
                  </Box>

                  <Grid container spacing={2} direction="column" sx={{ mt: 3, mb: 4, ml: 3 }}>
                    {addOrRemove === true && (
                      <Grid>
                        <Autocomplete
                          multiple
                          limitTags={2}
                          disableCloseOnSelect
                          options={crmTagsArr}
                          getOptionLabel={option => option.tagName}
                          slotProps={{
                            paper: {
                              sx: {
                                '& .MuiAutocomplete-option': {
                                  fontSize: 13,
                                  minHeight: 32,
                                },
                              },
                            },
                          }}
                          value={crmTagsArr.filter(tag => addCrmTags.includes(tag.tagId))}
                          onChange={(_, newValue) => setAddCrmTags(newValue ? newValue?.map(tag => tag.tagId) : [])}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.tagId} style={{height: '34px'}}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 2 }}
                                checked={selected}
                                sx={{
                                '& .MuiSvgIcon-root': {
                                  fontSize: 17,
                                },
                                  color: '#94a3b8',
                              }}
                              />
                              {option.tagName}
                            </li>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label="HL Add Tag"
                              size="small"
                              fullWidth
                              variant="outlined"
                              sx={{
                              bgcolor: '#fff',
                              borderRadius: 2,
                              '& .MuiOutlinedInput-root': {
                                fontSize: 14,
                                color: '#23244A',
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: '#dedede',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#3D52A0',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#3D52A0',
                                },
                              },

                              '& .MuiInputLabel-root': {
                                fontSize: 13,
                                color: '#23244A',
                              },
                            }}
                            />
                          )}
                          sx={{ width: '50%', bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(24,26,42,0.08)' }}
                        />
                      </Grid>
                    )}
                    <Grid>
                      <Autocomplete
                        multiple
                        limitTags={2}
                        disableCloseOnSelect
                        options={crmTagsArr}
                        getOptionLabel={option => option.tagName}
                        slotProps={{
                            paper: {
                              sx: {
                                '& .MuiAutocomplete-option': {
                                  fontSize: 13,
                                  minHeight: 32,
                                },
                              },
                            },
                          }}
                        value={crmTagsArr.filter(tag => removeCrmTags.includes(tag.tagId))}
                        onChange={(_, newValue) => setRemoveCrmTags(newValue ? newValue?.map(tag => tag.tagId) : [])}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.tagId} style={{height: '34px'}}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 2 }}
                              checked={selected}
                              sx={{
                                '& .MuiSvgIcon-root': {
                                  fontSize: 17,
                                },
                                  color: '#94a3b8',
                              }}
                            />
                            {option.tagName}
                          </li>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="HL Remove Tag"
                            size="small"
                            fullWidth
                            variant="outlined"
                            sx={{
                              bgcolor: '#fff',
                              borderRadius: 2,
                              '& .MuiOutlinedInput-root': {
                                fontSize: 14,
                                color: '#23244A',
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: '#dedede',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#3D52A0',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#3D52A0',
                                },
                              },

                              '& .MuiInputLabel-root': {
                                fontSize: 13,
                                color: '#23244A',
                              },
                            }}
                          />
                        )}
                        sx={{ width: '50%', bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(24,26,42,0.08)' }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </>
          )}
          
          <Box sx={{display: 'flex', gap: 1}}>
            <Box className="step-number">5</Box>
            <Box>
              <Box sx={{
                bgcolor: '#f3f9ff',
                px: 2,
                py: 1,
                borderRadius: 4,
              }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>
                Tag Notes <span style={{ fontWeight: 600, color: '#888' }}>(Optional)</span>
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#555' }}>
                Set options for tag mapping notes.
              </Typography>
            </Box>
            <Grid container spacing={2} direction="column" sx={{ mb: 3, ml: 3 }}>
              <Grid>
                <FormControl component="fieldset">
                  <FormControlLabel
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: 13,
                        color: '#475569',
                      },
                    }}
                    control={
                      <Checkbox
                        checked={tagOption === true}
                        onChange={e => setTagOption(e.target.checked ? true : false)}
                        size="small"
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: 18,
                          },
                            color: '#94a3b8',
                          }}
                      />
                    }
                    label="Enable and select a custom field below to add tag notes."
                  />
                </FormControl>
              </Grid>
              {tagOption === true && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      options={customFieldsArr}
                      getOptionLabel={option => option.fieldName}
                      value={customFieldsArr.find(f => f.fieldId === customField) || undefined}
                      onChange={(_, newValue) => setCustomField(newValue ? newValue.fieldId : '')}
                      slotProps={{
                        paper: {
                          sx: {
                            "& .MuiAutocomplete-option": {
                              fontSize: 13,
                              minHeight: 32,
                            },
                          },
                        },
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Custom Field"
                          size="small"
                          fullWidth
                          variant="outlined"
                          sx={{
                          bgcolor: '#fff',
                          borderRadius: 2,
                          '& .MuiOutlinedInput-root': {
                            fontSize: 14,
                            color: '#23244A',
                            borderRadius: 2,
                            '& fieldset': {
                              borderColor: '#dedede',
                            },
                            '&:hover fieldset': {
                              borderColor: '#3D52A0',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#3D52A0',
                            },
                          },

                          '& .MuiInputLabel-root': {
                            fontSize: 13,
                            color: '#23244A',
                          },
                        }}
                        />
                      )}
                      sx={{ width: '100%', bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(24,26,42,0.08)' }}
                      disableClearable
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Custom Field Value"
                      size="small"
                      fullWidth
                      variant="outlined"
                      placeholder="Enter a value"
                      value={customFieldValue}
                      onChange={(e) => setCustomFieldValue(e.target.value)}
                      sx={{
                        bgcolor: '#fff',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': {
                          fontSize: 14,
                          color: '#23244A',
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: '#dedede',
                          },
                          '&:hover fieldset': {
                            borderColor: '#3D52A0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3D52A0',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: 13,
                          color: '#23244A',
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Grid>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 2, background: '#FAFAFC' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={props.onClose}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            minWidth: 110,
            border: 'none',
            background: '#dedede',
            color: '#000',
            textTransform: 'capitalize',
            '&:hover': { background: '#d6d6d6' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            minWidth: 160,
            background: '#1eac53',
            textTransform: 'capitalize',
            '&:hover': { background: '#1a9849' },
          }}
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </DialogActions>
      {error && (
        <Box sx={{ color: '#d32f2f', textAlign: 'center', pb: 2, px: 3, fontWeight: 500 }}>
          {error}
        </Box>
      )}
    </Dialog>
  );
};

export default AddTagMapping;